import React, { HTMLAttributes, ReactElement, useEffect, useRef, useState } from 'react';
import './ToolTip.css';
import { IconClose } from '../../assets/icons/';
import IconLegacyInformation from '../../assets/icons/IconLegacyInformation';

export interface IToolTipProps extends HTMLAttributes<any> {
	notchPosition?:
		| 'bottom-center'
		| 'bottom-left'
		| 'bottom-right'
		| 'top-center'
		| 'top-left'
		| 'top-right';
	mobileNotchPosition?: IToolTipProps['notchPosition'];
	notInline?: boolean;
	fill?: string;
	children?: string | ReactElement | ReactElement[];
	alternate?: boolean;
}

const ToolTip = ({
	className = '',
	children,
	notchPosition = 'bottom-left',
	mobileNotchPosition = 'bottom-right',
	id,
	notInline = false,
	fill,
	alternate = false,
	...rest
}: IToolTipProps) => {
	const Tag = !notInline ? 'span' : 'div';
	const [closeTip, setCloseTip] = useState<boolean>(true);

	const tooltipRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			if (closeTip || !tooltipRef.current) {
				return
			};
			const tooltip = tooltipRef.current;
			const focusableElements = tooltip.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			);
			const firstElement = focusableElements[0] as HTMLElement;	
			const lastElement = focusableElements[focusableElements.length -1] as HTMLElement;

			const handleTrapFocus = (event: KeyboardEvent) => {
				if (event.key === 'Escape') {
					event.preventDefault();
					setCloseTip(true);
					return;
				}
				if (event.key ===  'Tab') {
					if (focusableElements.length === 1) {
						event.preventDefault();
						firstElement.focus();
					} else if (event.shiftKey && document.activeElement === firstElement ) {
						event.preventDefault();
						lastElement.focus();
					} else if (!event.shiftKey && document.activeElement === lastElement ) {
						event.preventDefault();
						firstElement.focus();
					} 
				}
			};
			tooltip.addEventListener("keydown", handleTrapFocus);
			firstElement.focus();

			return () => {
				tooltip.removeEventListener("keydown", handleTrapFocus);
			};

		}, [closeTip]);

	return (
		<Tag
			tabIndex={-1}
			onClick={(e) => e.preventDefault()}
			className={`ToolTip cursor-point relative w-4 h-4 inline-block outline-none ${className}`}
			ref = {triggerRef}
			aria-describedby= 'tooltip--content'
			{...rest}
			onMouseOver={() => {
				setCloseTip(false);
			}}
			onMouseOut={() => {
				setCloseTip(true);
			}}
			onFocus={() => {
				setCloseTip(false);
			}}
			onBlur={() => {
				setTimeout(()=> {
					const toFocus = document.activeElement;
					if (tooltipRef.current && !tooltipRef.current.contains(toFocus) && !triggerRef.current?.contains(toFocus)) {
						setCloseTip(true);
					}
				}, 0)
			}}
		>
			<IconLegacyInformation fill={fill} />

			<div
				className={`ToolTip__text_container cursor-text w-80 bg-white break-words flex p-4 absolute text-grey-tundora pr-8 ${notchPosition} mobile-${
					mobileNotchPosition || notchPosition
				}`}
				role = 'tooltip'
				id='tooltip--content'
				ref = {tooltipRef}
				style={{
					display: closeTip ? 'none' : 'flex',
				}}
				aria-hidden={closeTip? closeTip : 'false'}
			>
				{typeof children === 'string' ? (
					<p className='m-0'>{children}</p>
				) : (
					children
				)}
				<button
					// type='button'
					onClick={() => setCloseTip(true)}
					className='ToolTip__text_container__close_button right-2 absolute bg-white border-none cursor-pointer'
					title='Close tooltip'
				>
					<IconClose role='img' size='24' />
				</button>
			</div>
		</Tag>
	);
};

export default ToolTip;
